import React, {useState} from 'react';
import {Send} from 'lucide-react';
import {useQuotesApi} from "../context/ApiContext.tsx";
import SamButton from "./SamButton.tsx";
import CancelButton from "./CancelButton.tsx";
import {RespondQuoteFormData} from '../api/ApiTypes.ts';
import {createLocalDate, formatDateToString} from "../utils/dateUtils.ts";
import {Uad36Alert} from "./Uad36Notice.tsx";

interface RespondQuoteProps {
    onSuccess: () => void;
    onCancel: () => void;
    quoteId: number | null;
    suggestedFee?: number;
    uad36: boolean;
}

export const RespondQuoteForm: React.FC<RespondQuoteProps> = ({
                                                                  onSuccess,
                                                                  onCancel,
                                                                  quoteId,
                                                                  suggestedFee,
                                                                  uad36,
                                                              }) => {

    if (!quoteId) {
        return <div>Invalid Quote Id</div>;
    }

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [formData, setFormData] = useState<RespondQuoteFormData>({
        quoteId: quoteId ,
        estimatedDeliveryDate: formatDateToString(createLocalDate(null)),
        appraiserFee: suggestedFee? suggestedFee : 0,
        comment: ''
    });

    const [errors, setErrors] = useState<{
        appraiserFee?: string;
        estimatedDeliveryDate?: string;
        comment?: string;
    }>({});

    const quotesApi = useQuotesApi();

    const handleDeliveryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = createLocalDate(e.target.value);
        setFormData(prev => ({
            ...prev,
            estimatedDeliveryDate: formatDateToString(newDate)
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors: {
            estimatedDeliveryDate?: string;
            appraiserFee?: string;
            comment?: string;
        } = {};

        if (!formData.estimatedDeliveryDate) {
            newErrors.estimatedDeliveryDate = 'Please select an estimated delivery date.';
            isValid = false;
        }
        if (formData.appraiserFee === null) {
            newErrors.appraiserFee = 'Please enter an appraiser fee.';
            isValid = false;
        }
        if (!formData.comment.trim()) {
            newErrors.comment = 'Please enter a comment.';
            isValid = false;
        }
        setErrors(newErrors);

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            // Call your API to send the message
            await quotesApi.acceptQuote(formData);
            // Call onSuccess to notify parent component
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("Error sending message:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        // Prevent cancellation during submission
        if (!isSubmitting && onCancel) {
            onCancel();
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {uad36 && <Uad36Alert/>}
            <div>
                <label htmlFor="estimatedDeliveryDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Delivery Date
                </label>
                <input
                    id="estimatedDeliveryDate"
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.estimatedDeliveryDate}
                    onChange={handleDeliveryDateChange}
                    disabled={isSubmitting}
                />
                {errors.estimatedDeliveryDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.estimatedDeliveryDate}</p>
                )}
            </div>

            <div>
                <label htmlFor="appraiserFee" className="block text-sm font-medium text-gray-700 mb-1">
                    Appraiser Fee
                </label>
                <input
                    id="appraiserFee"
                    type="number"
                    name="appraiserFee"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter fee amount"
                    value={formData.appraiserFee}
                    onChange={handleChange}
                    disabled={isSubmitting}
                />
                {errors.appraiserFee && (
                    <p className="mt-1 text-sm text-red-600">{errors.appraiserFee}</p>
                )}
            </div>

            <div>
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                </label>
                <textarea
                    id="comment"
                    name="comment"
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Please enter your comment here ..."
                    value={formData.comment}
                    onChange={handleChange}
                    disabled={isSubmitting}
                />
                {errors.comment && (
                    <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
                )}
            </div>

            <div className="flex justify-end space-x-2">
                <CancelButton
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </CancelButton>
                <SamButton type="submit" disabled={ isSubmitting}>
                    <span className="inline-flex items-center space-x-1">
                        {isSubmitting ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-white rounded-full mr-1"></span>
                                <span>Sending...</span>
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4 mr-1" />
                                <span>Respond</span>
                            </>
                        )}
                    </span>
                </SamButton>
            </div>
        </form>
    );
};